import { useEffect, useRef } from "react";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * Interactive plasma warp shader — fragment-shader-driven background.
 * Mouse bends space toward itself (gravity lens); click sends a shockwave.
 * Palette crossfades between dark and light modes via u_theme (0 → 1).
 */

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform vec2  u_resolution;
uniform vec2  u_mouse;
uniform vec2  u_clickPos;
uniform float u_time;
uniform float u_clickAge;
uniform float u_theme;  // 0 = dark, 1 = light (smoothly lerped)

// --- value noise ---
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float vnoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  float a = hash(i); float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0)); float d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * vnoise(p); p *= 2.0; a *= 0.5; }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  vec2 p  = uv * 2.0 - 1.0;
  float aspect = u_resolution.x / u_resolution.y;
  p.x *= aspect;

  // cursor in same coord space
  vec2 m = u_mouse * 2.0 - 1.0;
  m.x *= aspect;

  // gravity-lens warp toward cursor
  vec2 toM  = m - p;
  float dM  = length(toM);
  vec2 warp = toM * 0.198 * exp(-dM * 1.4);
  p += warp;

  // click shockwave — 1.4s life, radial wave from click point
  if (u_clickAge < 1.4) {
    vec2 c = u_clickPos * 2.0 - 1.0; c.x *= aspect;
    vec2 toC = p - c;
    float dC = length(toC);
    float wave = sin(dC * 9.0 - u_clickAge * 12.0)
               * exp(-dC * 1.5 - u_clickAge * 2.2);
    p += normalize(toC + 0.0001) * wave * 0.12;
  }

  // plasma flow field
  float t  = u_time * 0.09;
  float n1 = fbm(p * 1.1 + vec2( t, -t * 0.6));
  float n2 = fbm(p * 2.2 + vec2(-t * 0.4, t * 0.3) + n1 * 0.8);
  float n3 = fbm(p * 0.5 + vec2( t * 0.2, t * 0.8) - n2 * 0.5);
  float plasma = (n1 + n2 * 0.55 + n3 * 0.3) / 1.85;

  // Dark palette — matches site brand in dark mode
  vec3 dDeep = vec3(0.055, 0.037, 0.094);  // ~#0E0918
  vec3 dMid  = vec3(0.445, 0.327, 0.620);  // ~#715399
  vec3 dHigh = vec3(0.667, 0.545, 0.855);  // ~#AA8BDA

  // Light palette — lavender-tinted cream base, softer lavender mid, muted plum high.
  // Dialed back from saturated purple to read calmer while still clearly reading as plasma.
  vec3 lDeep = vec3(0.950, 0.918, 0.965);  // ~#F2EAF6 near-cream with faint lavender tint
  vec3 lMid  = vec3(0.635, 0.530, 0.810);  // ~#A287CF soft lavender
  vec3 lHigh = vec3(0.340, 0.260, 0.525);  // ~#574286 muted plum

  vec3 cDeep = mix(dDeep, lDeep, u_theme);
  vec3 cMid  = mix(dMid,  lMid,  u_theme);
  vec3 cHigh = mix(dHigh, lHigh, u_theme);

  // Plasma range + highlight weight — slightly tighter in light than before so
  // the field isn't overwhelmingly saturated.
  float midLo    = mix(0.30, 0.24, u_theme);
  float midHi    = mix(0.62, 0.58, u_theme);
  float hiLo     = mix(0.72, 0.58, u_theme);
  float hiHi     = mix(0.95, 0.92, u_theme);
  float hiWeight = mix(0.42, 0.62, u_theme);

  vec3 color = mix(cDeep, cMid,  smoothstep(midLo, midHi, plasma));
  color      = mix(color, cHigh, smoothstep(hiLo, hiHi, plasma) * hiWeight);

  // Cursor "ink" — dark: add glow (brightens); light: mix toward lavender (darkens into smudge)
  float glow = exp(-dM * 2.2) * 0.35;
  vec3 darkGlow  = color + cHigh * glow;
  vec3 lightGlow = mix(color, cHigh, clamp(glow * 1.4, 0.0, 1.0));
  color = mix(darkGlow, lightGlow, u_theme);

  // Click pulse — same dual behavior
  if (u_clickAge < 0.5) {
    vec2 c = u_clickPos * 2.0 - 1.0; c.x *= aspect;
    float flash = exp(-length(p - c) * 3.0) * (1.0 - u_clickAge / 0.5);
    vec3 darkFlash  = color + cHigh * flash * 0.7;
    vec3 lightFlash = mix(color, cHigh, clamp(flash * 0.9, 0.0, 1.0));
    color = mix(darkFlash, lightFlash, u_theme);
  }

  // Vignette — strong in dark, barely-there in light (cream shouldn't edge-darken)
  float vig = smoothstep(1.5, 0.2, length(uv - 0.5) * 1.5);
  float vigScale = mix(0.42 + 0.58 * vig, 0.94 + 0.06 * vig, u_theme);
  color *= vigScale;

  // Overall brightness trim — only in dark mode
  color *= mix(0.9, 1.0, u_theme);

  gl_FragColor = vec4(color, 1.0);
}
`;

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error("[PlasmaWarp] shader error:", gl.getShaderInfoLog(s));
  }
  return s;
}

export default function PlasmaWarpBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();
  const themeTargetRef = useRef(theme === "light" ? 1 : 0);

  useEffect(() => {
    themeTargetRef.current = theme === "light" ? 1 : 0;
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", { antialias: false, premultipliedAlpha: false });
    if (!gl) return;

    const prog = gl.createProgram()!;
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER,   VERT));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, "a_pos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes       = gl.getUniformLocation(prog, "u_resolution");
    const uMouse     = gl.getUniformLocation(prog, "u_mouse");
    const uClickPos  = gl.getUniformLocation(prog, "u_clickPos");
    const uTime      = gl.getUniformLocation(prog, "u_time");
    const uClickAge  = gl.getUniformLocation(prog, "u_clickAge");
    const uTheme     = gl.getUniformLocation(prog, "u_theme");

    // Smoothly lerped theme — fades the palette on dark/light toggle
    let themeSmoothed = themeTargetRef.current;

    // target vs current mouse — gives us a lazy trail
    const tgt = { x: 0.5, y: 0.5 };
    const cur = { x: 0.5, y: 0.5 };
    const click = { x: 0.5, y: 0.5, time: -10 };

    const onMove = (e: MouseEvent) => {
      tgt.x = e.clientX / window.innerWidth;
      tgt.y = 1 - e.clientY / window.innerHeight; // flip for GL coords
    };
    const onDown = (e: MouseEvent) => {
      click.x = e.clientX / window.innerWidth;
      click.y = 1 - e.clientY / window.innerHeight;
      click.time = performance.now() / 1000;
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width  = Math.floor(window.innerWidth  * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width  = window.innerWidth  + "px";
      canvas.style.height = window.innerHeight + "px";
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("resize", resize);
    resize();

    const t0 = performance.now();
    let raf = 0;
    const tick = () => {
      // lazy cursor trail
      cur.x += (tgt.x - cur.x) * 0.05;
      cur.y += (tgt.y - cur.y) * 0.05;

      // ease theme toward its target — ~300ms crossfade on toggle
      themeSmoothed += (themeTargetRef.current - themeSmoothed) * 0.08;

      const now = performance.now() / 1000;
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform2f(uMouse, cur.x, cur.y);
      gl.uniform2f(uClickPos, click.x, click.y);
      gl.uniform1f(uTime, (performance.now() - t0) / 1000);
      gl.uniform1f(uClickAge, now - click.time);
      gl.uniform1f(uTheme, themeSmoothed);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div aria-hidden style={{ position: "fixed", inset: 0, zIndex: 0 }}>
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
    </div>
  );
}
